import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { authAPI } from '../lib/api'
import { Smartphone } from 'lucide-react'
import { CamsLogoMark } from '../components/CamsLogo'
import { isMobileDevice } from '../utils/deviceDetect'
import RoleChooserModal from '../components/RoleChooserModal'

export default function Login() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [pendingRoleChoice, setPendingRoleChoice] = useState(null) // { available_roles, dashboardPath }

  const { login, selectRole, logout } = useAuthStore()
  const navigate = useNavigate()
  const isMobile = isMobileDevice()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await login(username, password)
      if (result.success) {
        if (result.requires_role_choice) {
          // 양쪽 capability — 역할 선택 모달
          setPendingRoleChoice({
            available_roles: result.available_roles,
            user: result.user,
          })
        } else {
          // 단일 capability or 관리자 — 즉시 진입
          const role = result.user.active_role || result.user.role || result.user.user_type
          navigate(isMobile ? '/mobile/home' : getDashboardPath(role))
        }
      } else {
        setError(result.error || '로그인에 실패했습니다')
      }
    } catch (err) {
      setError(err?.message || '로그인에 실패했습니다')
    } finally {
      setLoading(false)
    }
  }

  const handleRoleSelected = async (role) => {
    const r = await selectRole(role)
    if (r.success) {
      setPendingRoleChoice(null)
      navigate(isMobile ? '/mobile/home' : getDashboardPath(role))
    } else {
      setError(r.error)
    }
  }

  const handleRoleCancel = async () => {
    await logout()
    setPendingRoleChoice(null)
  }

  // 역할 선택 모달 (양쪽 capability 협력사) — PC/모바일 공통 오버레이
  const roleChooser = pendingRoleChoice && (
    <RoleChooserModal
      availableRoles={pendingRoleChoice.available_roles}
      onSelect={handleRoleSelected}
      onCancel={handleRoleCancel}
    />
  )

  // 모바일 레이아웃 — 화면 꽉 채움, 큰 터치 타겟
  if (isMobile) {
    return (
      <div className="min-h-[100dvh] flex flex-col bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600">
        {roleChooser}
        {/* 상단 로고 영역 — 화면 40% */}
        <div className="flex-[2] flex flex-col items-center justify-center px-8">
          {/* Phase HZ: CAMS 회사 로고 */}
          <div className="w-24 h-24 bg-white/95 backdrop-blur rounded-3xl flex items-center justify-center mb-5 shadow-2xl">
            <CamsLogoMark size={68} />
          </div>
          <h1 className="text-4xl font-extrabold text-white tracking-tight">CAMS</h1>
          <p className="text-blue-100 mt-1 text-base">주식회사 캠스 · 금형관리 시스템</p>
          <p className="text-blue-200/70 mt-0.5 text-[10px] tracking-wide">Creative Auto Module System Co., Ltd.</p>
        </div>

        {/* 하단 로그인 폼 — 화면 60%, 흰색 라운드 카드 */}
        <div className="flex-[3] bg-white rounded-t-[2.5rem] px-7 pt-10 pb-8 flex flex-col">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">로그인</h2>
          <p className="text-base text-gray-500 mb-8">아이디와 비밀번호를 입력하세요</p>

          {error && (
            <div className="rounded-2xl bg-red-50 border border-red-200 p-4 mb-5">
              <p className="text-base text-red-600 text-center font-semibold">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5 flex-1">
            <div>
              <label className="block text-base font-semibold text-gray-800 mb-2">아이디</label>
              <input
                type="text" required placeholder="사번 또는 업체코드"
                value={username} onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl text-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
              />
            </div>
            <div>
              <label className="block text-base font-semibold text-gray-800 mb-2">비밀번호</label>
              <input
                type="password" required placeholder="비밀번호 입력"
                value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl text-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
              />
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-4 bg-blue-600 text-white font-bold rounded-2xl active:bg-blue-800 disabled:opacity-50 transition-all text-lg shadow-xl shadow-blue-600/30 active:scale-[0.97]">
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  로그인 중...
                </span>
              ) : '로그인'}
            </button>
          </form>

          {/* 하단 */}
          <p className="text-center text-sm text-gray-400 mt-auto pt-4">CAMS v2.0</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {roleChooser}
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 space-y-6">
        {/* Phase II: PC 로그인 카드 — 회사 로고 + 한글/영문 회사명 (샘플 디자인 적용) */}
        <div className="flex flex-col items-center">
          <img src="/cams-icon.png" alt="CAMS" className="w-20 h-20 object-contain mb-3" />
          <h2 className="text-2xl font-bold text-gray-900">CAMS 로그인</h2>
          <p className="text-sm text-gray-600 mt-1">금형관리 시스템</p>
          <p className="text-[10px] text-gray-400 mt-0.5 tracking-wide">Creative Auto Module System Co., Ltd.</p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          
          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <label htmlFor="username" className="sr-only">
                사용자명
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="input"
                placeholder="사용자명"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                비밀번호
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="input"
                placeholder="비밀번호"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? '로그인 중...' : '로그인'}
            </button>
          </div>

        </form>
      </div>
    </div>
  )
}

// 역할별 대시보드 경로 결정
function getDashboardPath(role) {
  switch (role) {
    case 'system_admin':
      return '/dashboard/admin'
    case 'mold_developer':
      return '/dashboard/developer'
    case 'staff':
      return '/dashboard/developer'
    case 'maker':
      return '/dashboard/maker'
    case 'plant':
      return '/dashboard/plant'
    default:
      return '/'
  }
}
